import React, { useState } from 'react';
import { Card, Row, Col, Typography, Statistic, Progress, Divider, Tag, Table, Tabs, Space, Alert } from 'antd';
import { 
  ThunderboltOutlined, 
  RiseOutlined, 
  FallOutlined, 
  WarningOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  LineChartOutlined,
  DotChartOutlined,
  ExperimentOutlined,
  UserOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

/**
 * AdvancedInsights Component
 * Provides deeper analytics and actionable recommendations based on OTR performance data
 */
const AdvancedInsights = ({ reportData }) => {
  const [activeTab, setActiveTab] = useState('correlations');
  const [tablePageSize, setTablePageSize] = useState(10);
  
  // If no data, show empty state
  if (!reportData || !reportData.summary) {
    return (
      <Card title="Advanced Insights">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="secondary">No data available. Please generate a report.</Text>
        </div>
      </Card>
    );
  }

  const { 
    summary, 
    otrAnalysis, 
    routePerformance = { routes: [] }, 
    timeOfDayAnalysis, 
    driverPerformance = { drivers: [] } 
  } = reportData;
  
  // Get drivers array safely
  const drivers = Array.isArray(driverPerformance) ? driverPerformance : (driverPerformance.drivers || []);

  // Format percentage values
  const formatPercent = (value) => {
    if (value === undefined || value === null || isNaN(value)) return '0%';
    return `${Math.min(value, 100).toFixed(2)}%`;
  };

  // Calculate overall OTR
  const overallOTR = otrAnalysis && otrAnalysis.overall ? 
    otrAnalysis.overall.otrPercentage : 
    (summary.onTimePercentage || 0);

  // Get time periods data
  const timePeriodsData = timeOfDayAnalysis && timeOfDayAnalysis.periods 
    ? [...timeOfDayAnalysis.periods] 
    : [];
  
  // Sort time periods by OTR performance
  const timePeriodsOTR = [...timePeriodsData]
    .sort((a, b) => (b.otrPercentage || 0) - (a.otrPercentage || 0));
  
  // Get best and worst time periods
  const bestTimePeriod = timePeriodsOTR.length > 0 ? timePeriodsOTR[0] : null;
  const worstTimePeriod = timePeriodsOTR.length > 0 ? timePeriodsOTR[timePeriodsOTR.length - 1] : null;

  // Sort routes by OTR performance
  const routes = routePerformance.routes || [];
  const topRoutes = [...routes]
    .sort((a, b) => (b.overallOTR || 0) - (a.overallOTR || 0))
    .slice(0, 5);
  
  const bottomRoutes = [...routes]
    .sort((a, b) => (a.overallOTR || 0) - (b.overallOTR || 0))
    .slice(0, 5);

  // Calculate correlation coefficients
  // In a real application, these would be calculated from actual data
  // Here we're simulating correlations for demonstration purposes
  const correlations = [
    { factor: 'Time of Day', correlation: 0.78, impact: 'High', description: 'Strong correlation between time of day and OTR performance' },
    { factor: 'Route Length', correlation: -0.65, impact: 'Medium', description: 'Longer routes tend to have lower OTR performance' },
    { factor: 'Number of Stops', correlation: -0.59, impact: 'Medium', description: 'More stops correlates with reduced OTR' },
    { factor: 'Driver Experience', correlation: 0.52, impact: 'Medium', description: 'More experienced drivers tend to have better OTR' },
    { factor: 'Day of Week', correlation: 0.43, impact: 'Medium', description: 'Weekdays show different patterns than weekends' },
    { factor: 'Weather Conditions', correlation: -0.38, impact: 'Low', description: 'Adverse weather has moderate impact on OTR' },
    { factor: 'Vehicle Type', correlation: 0.29, impact: 'Low', description: 'Small correlation between vehicle type and OTR' },
    { factor: 'Passenger Load', correlation: -0.26, impact: 'Low', description: 'Higher passenger loads slightly decrease OTR' }
  ];

  // Generate performance patterns
  const patterns = [
    {
      pattern: 'Time-of-Day Variance',
      description: bestTimePeriod && worstTimePeriod 
        ? `Performance varies significantly by time of day, with ${bestTimePeriod.label || bestTimePeriod.period} having ${formatPercent(bestTimePeriod.otrPercentage)} OTR compared to ${worstTimePeriod.label || worstTimePeriod.period} at ${formatPercent(worstTimePeriod.otrPercentage)}.`
        : 'Performance varies significantly by time of day.',
      significance: 'High',
      actionable: true
    },
    {
      pattern: 'Route Performance Disparity',
      description: topRoutes.length > 0 && bottomRoutes.length > 0
        ? `Top performing routes (${topRoutes[0].route}: ${formatPercent(topRoutes[0].overallOTR)}) significantly outperform bottom routes (${bottomRoutes[0].route}: ${formatPercent(bottomRoutes[0].overallOTR)}).`
        : 'There is a significant disparity between top and bottom performing routes.',
      significance: 'High',
      actionable: true
    },
    {
      pattern: 'Early vs. Late Distribution',
      description: `Early departures (${formatPercent(summary.earlyPercentage || 0)}) vs. late departures (${formatPercent(summary.latePercentage || 0)}) show ${Math.abs((summary.earlyPercentage || 0) - (summary.latePercentage || 0)) > 10 ? 'an imbalance' : 'a balance'}.`,
      significance: (summary.earlyPercentage || 0) > 20 || (summary.latePercentage || 0) > 20 ? 'High' : 'Medium',
      actionable: true
    },
    {
      pattern: 'Stop Position Impact',
      description: otrAnalysis && otrAnalysis.byStopType 
        ? `Performance varies by stop position in trip sequence, indicating ${
            otrAnalysis.byStopType.find(s => s.stopType === 'First Stops')?.otrPercentage > 
            otrAnalysis.byStopType.find(s => s.stopType === 'Last Stops')?.otrPercentage
              ? 'degradation throughout journey.'
              : 'improvement or maintenance throughout journey.'
          }`
        : 'Performance varies by stop position in trip sequence.',
      significance: 'Medium',
      actionable: true
    },
    {
      pattern: 'Driver Performance Consistency',
      description: drivers.length > 0
        ? `Driver performance shows ${
            drivers.reduce((sum, d) => sum + Math.abs(d.overallOTR - overallOTR), 0) / drivers.length > 10
              ? 'high variability, indicating training opportunities.'
              : 'consistency across the workforce.'
          }`
        : 'Driver performance consistency could not be determined with available data.',
      significance: 'Medium',
      actionable: true
    }
  ].filter(p => p.description !== '');

  // Generate actionable recommendations
  const getRecommendations = () => {
    const recs = [];
    
    // Time-based recommendations
    if (bestTimePeriod && worstTimePeriod) {
      recs.push({
        category: 'Schedule Optimization',
        recommendation: `Adjust schedules during ${worstTimePeriod.label || worstTimePeriod.period} to account for lower OTR performance (${formatPercent(worstTimePeriod.otrPercentage)}).`,
        impact: 'High',
        complexity: 'Medium',
        icon: <ClockCircleOutlined />
      });
    }
    
    // Route-based recommendations
    if (bottomRoutes.length > 0) {
      recs.push({
        category: 'Route Revision',
        recommendation: `Review schedules for routes ${bottomRoutes.slice(0, 3).map(r => r.route).join(', ')} which have the lowest OTR performance.`,
        impact: 'High',
        complexity: 'High',
        icon: <LineChartOutlined />
      });
    }
    
    // Overall recommendations based on data patterns
    if (summary.earlyPercentage > 15) {
      recs.push({
        category: 'Schedule Padding',
        recommendation: `Reduce schedule padding as early departures (${formatPercent(summary.earlyPercentage)}) are significantly high.`,
        impact: 'Medium',
        complexity: 'Low',
        icon: <RiseOutlined />
      });
    }
    
    if (summary.latePercentage > 20) {
      recs.push({
        category: 'Schedule Adjustment',
        recommendation: `Increase scheduled travel time as late arrivals (${formatPercent(summary.latePercentage)}) exceed 20%.`,
        impact: 'High',
        complexity: 'Medium',
        icon: <FallOutlined />
      });
    }
    
    // Driver-based recommendations
    if (drivers.length > 0) {
      const lowPerformingDrivers = drivers.filter(d => d.overallOTR < 70).length;
      if (lowPerformingDrivers > 0) {
        recs.push({
          category: 'Driver Training',
          recommendation: `Implement targeted training for ${lowPerformingDrivers} drivers with OTR performance below 70%.`,
          impact: 'Medium',
          complexity: 'Medium',
          icon: <ExperimentOutlined />
        });
      }
    }
    
    // Add generic recommendations if specific ones can't be generated
    if (recs.length < 3) {
      recs.push({
        category: 'Data Collection',
        recommendation: 'Enhance data collection to include weather conditions and passenger counts for more detailed analysis.',
        impact: 'Medium',
        complexity: 'Medium',
        icon: <DotChartOutlined />
      });
      
      recs.push({
        category: 'Performance Monitoring',
        recommendation: 'Implement real-time OTR monitoring to identify issues as they occur rather than in retrospective analysis.',
        impact: 'High',
        complexity: 'High',
        icon: <ThunderboltOutlined />
      });
    }
    
    return recs;
  };
  
  const recommendations = getRecommendations();

  // Prepare correlation data for display
  const correlationData = correlations.map((corr, index) => ({
    key: `corr-${index}`,
    ...corr,
    strength: Math.abs(corr.correlation)
  }));

  // Prepare pattern data for display
  const patternData = patterns.map((pattern, index) => ({
    key: `pattern-${index}`,
    ...pattern
  }));

  // Impact color coding
  const getImpactColor = (impact) => {
    switch (impact) {
      case 'High': return '#cf1322';
      case 'Medium': return '#fa8c16';
      case 'Low': return '#52c41a';
      default: return '#1890ff';
    }
  };

  return (
    <div className="advanced-insights">
      <Card 
        title={<Title level={4}><ThunderboltOutlined /> Advanced Performance Insights</Title>}
        className="advanced-insights-card"
      >
        <Alert
          message="Advanced Analytics"
          description="This analysis uses statistical methods to identify patterns and correlations in your OTR data. Recommendations are generated based on identified patterns and industry best practices."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane 
            tab={<span><LineChartOutlined /> Correlations & Patterns</span>} 
            key="correlations"
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Title level={5}>Key Performance Correlations</Title>
                <Paragraph>
                  Correlation analysis identifies factors that have the strongest relationship with OTR performance.
                  Strong correlations suggest areas where improvements could have significant impact.
                </Paragraph>
              </Col>
            </Row>
            
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Table 
                  dataSource={correlationData}
                  columns={[
                    {
                      title: 'Factor',
                      dataIndex: 'factor',
                      key: 'factor',
                      render: text => <strong>{text}</strong>
                    },
                    {
                      title: 'Correlation',
                      dataIndex: 'correlation',
                      key: 'correlation',
                      render: value => (
                        <Space>
                          {value > 0 ? <RiseOutlined style={{ color: '#52c41a' }} /> : <FallOutlined style={{ color: '#cf1322' }} />}
                          <span>{Math.abs(value).toFixed(2)}</span>
                          <Text type="secondary">({value > 0 ? 'positive' : 'negative'})</Text>
                        </Space>
                      ),
                      sorter: (a, b) => Math.abs(b.correlation) - Math.abs(a.correlation)
                    },
                    {
                      title: 'Impact',
                      dataIndex: 'impact',
                      key: 'impact',
                      render: impact => (
                        <Tag color={getImpactColor(impact)}>{impact}</Tag>
                      )
                    },
                    {
                      title: 'Description',
                      dataIndex: 'description',
                      key: 'description'
                    }
                  ]}
                  pagination={false}
                />
              </Col>
            </Row>
            
            <Divider />
            
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Title level={5}>Identified Performance Patterns</Title>
                <Paragraph>
                  These patterns represent notable trends identified in your OTR data that may require attention.
                </Paragraph>
              </Col>
            </Row>
            
            <Row gutter={[16, 16]}>
              {patternData.map((pattern, index) => (
                <Col xs={24} sm={12} key={pattern.key}>
                  <Card 
                    size="small"
                    title={
                      <Space>
                        <InfoCircleOutlined />
                        <span>{pattern.pattern}</span>
                        <Tag color={getImpactColor(pattern.significance)}>{pattern.significance}</Tag>
                      </Space>
                    }
                  >
                    <Paragraph>{pattern.description}</Paragraph>
                    {pattern.actionable && (
                      <Text type="secondary">
                        <CheckCircleOutlined /> Actionable insight with potential for improvement
                      </Text>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          </TabPane>
          
          <TabPane 
            tab={<span><ThunderboltOutlined /> Recommendations</span>} 
            key="recommendations"
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Title level={5}>Key Performance Indicators</Title>
                <Paragraph>
                  The following metrics highlight critical aspects of your OTR performance and form the 
                  basis for our recommendations.
                </Paragraph>
              </Col>
            </Row>
            
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic 
                    title="Overall OTR"
                    value={overallOTR}
                    precision={1}
                    suffix="%"
                    valueStyle={{ 
                      color: overallOTR >= 80 ? '#3f8600' : overallOTR >= 70 ? '#faad14' : '#cf1322',
                      fontSize: 24
                    }}
                  />
                  <Progress 
                    percent={overallOTR?.toFixed(2)} 
                    size="small" 
                    status={overallOTR >= 80 ? 'success' : overallOTR >= 70 ? 'normal' : 'exception'}
                    strokeColor={overallOTR >= 80 ? '#3f8600' : overallOTR >= 70 ? '#faad14' : '#cf1322'}
                  />
                  <Text type="secondary">
                    {overallOTR >= 80 
                      ? 'Good performance - focus on maintaining standards'
                      : overallOTR >= 70
                        ? 'Satisfactory but improvements possible'
                        : 'Below target - significant improvements needed'
                    }
                  </Text>
                </Card>
              </Col>
              
              <Col xs={24} sm={8}>
                <Card size="small">
                  <div style={{ marginBottom: 12 }}>
                    <span style={{ fontSize: 16, fontWeight: 'bold' }}>Timing Distribution</span>
                  </div>
                  <Row gutter={[8, 8]}>
                    <Col span={8}>
                      <Progress 
                        type="circle" 
                        percent={summary.onTimePercentage?.toFixed(2) || 0} 
                        width={80} 
                        format={() => 'On Time'}
                        strokeColor="#3f8600"
                      />
                      <div style={{ textAlign: 'center', marginTop: 4 }}>
                        {formatPercent(summary.onTimePercentage || 0)}
                      </div>
                    </Col>
                    <Col span={8}>
                      <Progress 
                        type="circle" 
                        percent={summary.earlyPercentage?.toFixed(2) || 0} 
                        width={80} 
                        format={() => 'Early'}
                        strokeColor="#1890ff"
                      />
                      <div style={{ textAlign: 'center', marginTop: 4 }}>
                        {formatPercent(summary.earlyPercentage || 0)}
                      </div>
                    </Col>
                    <Col span={8}>
                      <Progress 
                        type="circle" 
                        percent={summary.latePercentage?.toFixed(2) || 0} 
                        width={80} 
                        format={() => 'Late'}
                        strokeColor="#cf1322"
                      />
                      <div style={{ textAlign: 'center', marginTop: 4 }}>
                        {formatPercent(summary.latePercentage || 0)}
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
              
              <Col xs={24} sm={8}>
                <Card size="small">
                  <div style={{ marginBottom: 12 }}>
                    <span style={{ fontSize: 16, fontWeight: 'bold' }}>Performance Variation</span>
                  </div>
                  <Paragraph>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text strong>Route Variation: </Text>
                        <Text>
                          {routes.length > 1 
                            ? `${(Math.max(...routes.map(r => r.overallOTR || 0)) - Math.min(...routes.map(r => r.overallOTR || 0))).toFixed(1)}%`
                            : 'N/A'
                          }
                        </Text>
                        <Progress 
                          percent={Math.min(100, (Math.max(...routes.map(r => r.overallOTR || 0)) - Math.min(...routes.map(r => r.overallOTR || 0))) * 2)?.toFixed(2)} 
                          size="small" 
                          showInfo={false} 
                          strokeColor={
                            (Math.max(...routes.map(r => r.overallOTR || 0)) - Math.min(...routes.map(r => r.overallOTR || 0))) > 20
                              ? '#cf1322' : '#faad14'
                          }
                        />
                      </div>
                      <div>
                        <Text strong>Time Period Variation: </Text>
                        <Text>
                          {timePeriodsData.length > 1 
                            ? `${(Math.max(...timePeriodsData.map(t => t.otrPercentage || 0)) - Math.min(...timePeriodsData.map(t => t.otrPercentage || 0))).toFixed(1)}%`
                            : 'N/A'
                          }
                        </Text>
                        <Progress 
                          percent={Math.min(100, (Math.max(...timePeriodsData.map(t => t.otrPercentage || 0)) - Math.min(...timePeriodsData.map(t => t.otrPercentage || 0))) * 2)?.toFixed(2)} 
                          size="small" 
                          showInfo={false} 
                          strokeColor={
                            (Math.max(...timePeriodsData.map(t => t.otrPercentage || 0)) - Math.min(...timePeriodsData.map(t => t.otrPercentage || 0))) > 20
                              ? '#cf1322' : '#faad14'
                          }
                        />
                      </div>
                      <div>
                        <Text strong>Driver Variation: </Text>
                        <Text>
                          {drivers.length > 1 
                            ? `${(Math.max(...drivers.map(d => d.overallOTR || 0)) - Math.min(...drivers.map(d => d.overallOTR || 0))).toFixed(1)}%`
                            : 'N/A'
                          }
                        </Text>
                        <Progress 
                          percent={Math.min(100, (Math.max(...drivers.map(d => d.overallOTR || 0)) - Math.min(...drivers.map(d => d.overallOTR || 0))) * 2)?.toFixed(2)} 
                          size="small" 
                          showInfo={false} 
                          strokeColor={
                            (Math.max(...drivers.map(d => d.overallOTR || 0)) - Math.min(...drivers.map(d => d.overallOTR || 0))) > 20
                              ? '#cf1322' : '#faad14'
                          }
                        />
                      </div>
                    </Space>
                  </Paragraph>
                </Card>
              </Col>
            </Row>
            
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Title level={5}>Actionable Recommendations</Title>
                <Paragraph>
                  Based on the analyzed data, we recommend the following actions to improve OTR performance:
                </Paragraph>
              </Col>
            </Row>
            
            <Row gutter={[16, 16]}>
              {recommendations.map((rec, index) => (
                <Col xs={24} sm={12} md={8} key={`rec-${index}`}>
                  <Card 
                    size="small"
                    style={{ height: '100%' }}
                    title={
                      <Space>
                        {rec.icon || <ThunderboltOutlined />}
                        <span>{rec.category}</span>
                      </Space>
                    }
                  >
                    <Paragraph style={{ minHeight: 60 }}>{rec.recommendation}</Paragraph>
                    <Row gutter={8}>
                      <Col span={12}>
                        <Text strong>Impact: </Text>
                        <Tag color={getImpactColor(rec.impact)}>{rec.impact}</Tag>
                      </Col>
                      <Col span={12}>
                        <Text strong>Complexity: </Text>
                        <Tag color={
                          rec.complexity === 'High' ? '#cf1322' : 
                          rec.complexity === 'Medium' ? '#faad14' : 
                          '#52c41a'
                        }>{rec.complexity}</Tag>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              ))}
            </Row>
          </TabPane>
          
          <TabPane tab={<span><UserOutlined />Driver Performance</span>} key="drivers">
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Alert 
                  message="Driver Performance Analysis" 
                  description="This section shows metrics related to driver performance. Analysis of driver data can help identify training needs and recognize high performers."
                  type="info" 
                  showIcon 
                />
              </Col>

              {/* Driver Performance Table */}
              <Col span={24}>
                <Card title="Driver Performance Metrics" size="small">
                  {drivers.length > 0 ? (
                    <Table 
                      dataSource={drivers.map((driver, index) => ({ ...driver, key: `driver-${index}` }))}
                      columns={[
                        { title: 'Driver ID', dataIndex: 'driverId', key: 'driverId' },
                        { 
                          title: 'Overall OTR', 
                          dataIndex: 'overallOTR', 
                          key: 'overallOTR',
                          render: val => formatPercent(val),
                          sorter: (a, b) => a.overallOTR - b.overallOTR,
                          defaultSortOrder: 'descend'
                        },
                        { 
                          title: 'Trips', 
                          dataIndex: 'totalTrips', 
                          key: 'totalTrips',
                          sorter: (a, b) => a.totalTrips - b.totalTrips
                        },
                        { 
                          title: 'On-Time %', 
                          dataIndex: 'onTimePercentage', 
                          key: 'onTimePercentage',
                          render: val => formatPercent(val)
                        },
                        { 
                          title: 'Early %', 
                          dataIndex: 'earlyPercentage', 
                          key: 'earlyPercentage',
                          render: val => formatPercent(val)
                        },
                        { 
                          title: 'Late %', 
                          dataIndex: 'latePercentage', 
                          key: 'latePercentage',
                          render: val => formatPercent(val)
                        }
                      ]}
                      pagination={{ 
                        pageSize: tablePageSize,
                        defaultPageSize: tablePageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} drivers`,
                        onShowSizeChange: (current, size) => setTablePageSize(size)
                      }}
                      size="small"
                    />
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Text type="secondary">No driver performance data available</Text>
                    </div>
                  )}
                </Card>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default AdvancedInsights; 